export type BewegungsdatenRole = 'visitor' | 'exhibitor';

export interface BewegungsdatenColumnDef {
  key: string;
  header: string;
}

const SHARED_COLUMN_DEFS: BewegungsdatenColumnDef[] = [
  { key: 'passport', header: 'Passeport' },
  { key: 'linkedin', header: 'LinkedIn' },
  { key: 'birthdate', header: 'Date de naissance' },
  { key: 'hashtag', header: 'Hashtag' },
  { key: 'allergies', header: 'Allergies' },
  { key: 'nahrungsmittelunvertraglichkeit', header: 'Intolérances alimentaires' },
  { key: 'langulage_guide', header: 'Langues visites guidées' },
  { key: 'zielgruppe_angebote', header: 'Groupe cible' },
  { key: 'type_anreise', header: "Mode d'arrivée" },
  { key: 'departure_location', header: 'Lieu de départ' },
  { key: 'arrival_departure_time', header: "Heure de départ (arrivée)" },
  { key: 'arrival_time', header: "Heure d'arrivée" },
  { key: 'arrival_plane_selber', header: "J'organise mon arrivée" },
  { key: 'arrival_plane_atout', header: 'Voyage Atout France' },
  { key: 'parking_space', header: 'Place de parking' },
  { key: 'arrival_day_before', header: 'Arrivée la veille' },
  { key: 'departure_type_departure', header: 'Mode de départ' },
  { key: 'departure_time', header: 'Heure de départ' },
  { key: 'departure_plane_selber', header: "J'organise mon départ" },
  { key: 'haftungsfreistellung', header: 'Décharge de responsabilité' },
];

const VISITOR_ONLY_COLUMN_DEFS: BewegungsdatenColumnDef[] = [
  { key: 'join_inforeise', header: 'Inforeise' },
  { key: 'inforeise_rank1', header: 'Choix Inforeise 1' },
  { key: 'inforeise_rank2', header: 'Choix Inforeise 2' },
  { key: 'inforeise_rank3', header: 'Choix Inforeise 3' },
];

const EXHIBITOR_ONLY_COLUMN_DEFS: BewegungsdatenColumnDef[] = [
  { key: 'lunch1', header: 'Déjeuner jour 1' },
  { key: 'lunch2', header: 'Déjeuner jour 2' },
  { key: 'lunch3', header: 'Déjeuner jour 3' },
  { key: 'nimes_visit', header: 'Visite Nîmes' },
];

const LANGUAGE_GUIDE_LABELS: Record<string, string> = {
  '1': 'Allemand',
  '2': 'Anglais',
  '3': 'Français',
  '4': 'Allemand, Français',
  '5': 'Anglais, Français',
  '6': 'Allemand, Anglais',
  '7': 'Français, Anglais, Allemand',
};

const TRAVEL_TYPE_LABELS: Record<string, string> = {
  Zug: 'Train',
  Flug: 'Avion',
  Auto: 'Voiture',
};

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === -1 || value === '-1';
}

/** Yes/no for Excel: Oui or dash. */
function toOuiDash(value: unknown): string {
  return isTruthyFlag(value) ? 'Oui' : '-';
}

function toCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return toOuiDash(value);
  }
  return String(value);
}

function mapLanguageGuide(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const key = String(value);
  return LANGUAGE_GUIDE_LABELS[key] ?? key;
}

function mapTravelType(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const key = String(value);
  return TRAVEL_TYPE_LABELS[key] ?? key;
}

export function bewegungsdatenColumnDefs(role: BewegungsdatenRole): BewegungsdatenColumnDef[] {
  if (role === 'visitor') {
    return [...SHARED_COLUMN_DEFS, ...VISITOR_ONLY_COLUMN_DEFS];
  }
  return [...SHARED_COLUMN_DEFS, ...EXHIBITOR_ONLY_COLUMN_DEFS];
}

export function bewegungsdatenColumnKeys(role: BewegungsdatenRole): string[] {
  return bewegungsdatenColumnDefs(role).map((col) => col.key);
}

export function bewegungsdatenColumnHeaders(role: BewegungsdatenRole): string[] {
  return bewegungsdatenColumnDefs(role).map((col) => col.header);
}

/** Flatten nested firststart Bewegungsdaten (`t_profile.json`) onto top-level row keys for table/Excel. */
export function flattenBewegungsdaten(
  json: any,
  role: BewegungsdatenRole
): Record<string, string> {
  const data = json && typeof json === 'object' ? json : {};
  const arrival = data.arrival && typeof data.arrival === 'object' ? data.arrival : {};
  const departure = data.departure && typeof data.departure === 'object' ? data.departure : {};
  const event = data.event && typeof data.event === 'object' ? data.event : {};
  const postEvent = data.post_event && typeof data.post_event === 'object' ? data.post_event : {};

  const flat: Record<string, string> = {
    passport: toCellValue(data.passport),
    linkedin: toCellValue(data.linkedin),
    birthdate: toCellValue(data.birthdate),
    hashtag: toCellValue(data.hashtag),
    allergies: toCellValue(data.allergies),
    nahrungsmittelunvertraglichkeit: toCellValue(data.nahrungsmittelunvertraglichkeit),
    langulage_guide: mapLanguageGuide(data.langulage_guide),
    zielgruppe_angebote: toCellValue(data.zielgruppe_angebote),
    type_anreise: mapTravelType(arrival.type_anreise),
    departure_location: toCellValue(arrival.departure_location),
    arrival_departure_time: toCellValue(arrival.arrival_departure_time),
    arrival_time: toCellValue(arrival.arrival_time),
    arrival_plane_selber: toOuiDash(arrival.arrival_plane_selber),
    arrival_plane_atout: toOuiDash(arrival.arrival_plane_atout),
    parking_space: toOuiDash(arrival.parking_space),
    arrival_day_before: toOuiDash(arrival.arrival_day_before),
    departure_type_departure: mapTravelType(departure.departure_type_departure),
    departure_time: toCellValue(departure.departure_time),
    departure_plane_selber: toOuiDash(departure.departure_plane_selber),
    haftungsfreistellung: toOuiDash(postEvent.haftungsfreistellung),
  };

  if (role === 'visitor') {
    flat.join_inforeise = toOuiDash(postEvent.join_inforeise);
    flat.inforeise_rank1 = toCellValue(postEvent.inforeise_rank1);
    flat.inforeise_rank2 = toCellValue(postEvent.inforeise_rank2);
    flat.inforeise_rank3 = toCellValue(postEvent.inforeise_rank3);
  } else {
    flat.lunch1 = toOuiDash(event.lunch1);
    flat.lunch2 = toOuiDash(event.lunch2);
    flat.lunch3 = toOuiDash(event.lunch3);
    flat.nimes_visit = toOuiDash(event.nimes_visit);
  }

  return flat;
}
